import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Rocket, Sparkles } from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { DeployBadge, shortSha, timeAgo } from './ui';

/** One environment's latest deploy. Nothing is shown until the status has loaded. */
const EnvStatus = ({ label, loaded, deploy, head, url }) => (
  <div className="mse-env">
    <span className="mse-env-label">{label}</span>
    {loaded && <DeployBadge deploy={deploy} />}
    {head && (
      <a className="mse-env-sha" href={head.url} target="_blank" rel="noopener noreferrer" title={head.message}>
        {shortSha(head.sha)}
      </a>
    )}
    {deploy?.updatedAt && <span className="mse-env-time">{timeAgo(deploy.updatedAt)}</span>}
    {url && (
      <a className="mse-env-link" href={url} target="_blank" rel="noopener noreferrer">
        {url.replace(/^https?:\/\//, '')}
      </a>
    )}
  </div>
);

const changes = (n) => `${n} change${n === 1 ? '' : 's'}`;

const Summary = ({ status, short = false }) => {
  if (!status) return <span className="mse-release-summary text-muted">Loading release status</span>;
  if (status.behindBy > 0) {
    return (
      <span className="mse-release-summary text-danger">
        <AlertTriangle size={14} />
        {short
          ? 'Production has extra commits'
          : `Production has ${status.behindBy} commit${status.behindBy === 1 ? '' : 's'} that the test site does not. Resolve in git.`}
      </span>
    );
  }
  if (status.aheadBy > 0) {
    return (
      <span className="mse-release-summary">
        {short ? `${changes(status.aheadBy)} not in production` : `${changes(status.aheadBy)} on the test site not yet in production`}
      </span>
    );
  }
  return (
    <span className="mse-release-summary text-muted">
      {short ? 'Production is up to date' : 'Production is up to date with the test site'}
    </span>
  );
};

/** Pages missing from sitemap.xml (or dead entries in it): a nudge, never a block. */
export const sitemapProblems = (sitemap) =>
  sitemap?.present ? (sitemap.missing?.length || 0) + (sitemap.stale?.length || 0) : 0;

const ReleaseBar = ({ status, error, canEdit, onDeployClick, sitemap, onSitemapClick }) => {
  const sitemapCount = sitemapProblems(sitemap);
  // Phones get one row: a short summary that opens the environment details, and short action labels
  const isMobile = useIsMobile();
  const [details, setDetails] = useState(false);
  let disabledReason = null;
  if (!canEdit) disabledReason = 'Deploying needs the ADMIN role';
  else if (!status) disabledReason = 'Loading release status';
  else if (!status.canDeploy) {
    disabledReason = status.behindBy > 0 ? 'Production has commits the test site does not' : 'Nothing to deploy';
  }

  const environments = (
    <>
      <EnvStatus label="Test site (main)" loaded={!!status} deploy={status?.testDeploy} head={status?.mainHead} url={status?.siteUrl} />
      <EnvStatus label="Production" loaded={!!status} deploy={status?.prodDeploy} head={status?.prodHead} url={status?.prodUrl} />
    </>
  );
  const summary = error ? <span className="text-danger">{error}</span> : <Summary status={status} short={isMobile} />;

  return (
    <div className="mse-release">
      {isMobile ? (
        <button
          type="button"
          className="mse-link-button text-reset d-inline-flex align-items-center gap-1 text-start"
          style={{ flex: '1 1 0', minWidth: 0, fontSize: 13 }}
          onClick={() => setDetails((v) => !v)}
          aria-expanded={details}
          title="Show the test site and production deploys"
        >
          {details ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {summary}
        </button>
      ) : (
        <>
          {environments}
          <div className="mse-release-center">{summary}</div>
        </>
      )}
      {/* One group, so the actions wrap together and stay on the right rather than leaving Deploy alone on a row */}
      <div className="mse-release-actions">
        {sitemapCount > 0 && (
          <button type="button" className="mse-release-chip" onClick={onSitemapClick} title="Open Site health, Sitemap and llms.txt">
            <AlertTriangle size={13} /> Sitemap {isMobile ? '' : 'out of date '}({sitemapCount})
          </button>
        )}
        <Link
          to="/marketing-site/agent"
          className="btn btn-sm btn-outline-secondary"
          title="The AI marketing site agent"
          aria-label={isMobile ? 'AI assistant' : undefined}
        >
          <Sparkles size={14} className={isMobile ? undefined : 'me-1'} />
          {!isMobile && 'AI assistant'}
        </Link>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={onDeployClick}
          disabled={!!disabledReason}
          title={disabledReason || 'Publish everything on the test site to production'}
        >
          <Rocket size={14} className="me-1" />
          {isMobile ? 'Deploy' : 'Deploy to production'}
        </Button>
      </div>
      {isMobile && details && <div className="d-flex flex-column gap-1 w-100">{environments}</div>}
    </div>
  );
};

export default ReleaseBar;
